export const PROJECT_CAPABILITIES = Object.freeze([
  {
    key: "memberships",
    label: "Membresías",
    description: "Vigencias y renovaciones sobre los clientes existentes.",
    composesWith: Object.freeze(["CRM", "Cobros", "Gestión"]),
  },
  {
    key: "gastronomicMenu",
    label: "Carta gastronómica",
    description: "Carta construida sobre los mismos ítems y catálogo del proyecto.",
    composesWith: Object.freeze(["Landing", "Catálogo", "Pedidos"]),
  },
  {
    key: "tables",
    label: "Mesas",
    description: "Organización de mesas dentro de la operación existente.",
    composesWith: Object.freeze(["Gestión", "Pedidos"]),
  },
  {
    key: "tableReservations",
    label: "Reservas de mesa",
    description: "Reservas que reutilizan agenda, disponibilidad y clientes.",
    composesWith: Object.freeze(["Agenda", "Mesas", "CRM"]),
  },
  {
    key: "kitchenOrders",
    label: "Comandas",
    description: "Flujo de preparación conectado a pedidos, mesas y gestión.",
    composesWith: Object.freeze(["Pedidos", "Mesas", "Gestión"]),
  },
  {
    key: "recipes",
    label: "Recetas",
    description: "Composición de ítems con insumos y stock trazable.",
    composesWith: Object.freeze(["Catálogo", "Gestión"]),
  },
]);

const CAPABILITY_KEYS = PROJECT_CAPABILITIES.map(({ key }) => key);

export function normalizeProjectCapabilities(capabilities = {}) {
  const source = capabilities && typeof capabilities === "object" ? capabilities : {};
  return Object.fromEntries(
    CAPABILITY_KEYS.map((key) => [key, source?.[key] === true]),
  );
}

export function isProjectCapabilityEnabled(project, capabilityKey) {
  if (!CAPABILITY_KEYS.includes(String(capabilityKey || ""))) return false;
  return normalizeProjectCapabilities(project?.capabilities)[capabilityKey] === true;
}

export function activeProjectCapabilities(project) {
  const capabilities = normalizeProjectCapabilities(project?.capabilities);
  return PROJECT_CAPABILITIES.filter(({ key }) => capabilities[key]);
}

export const PROJECT_BUSINESS_PROFILES = Object.freeze([
  {
    key: "commerce",
    label: "Comercio",
    description: "Catálogo, pedidos y cobros sin sumar funciones específicas por rubro.",
    suggestedCapabilities: Object.freeze([]),
  },
  {
    key: "services",
    label: "Servicios",
    description: "Landing, agenda, CRM y cobros sin imponer una estructura rígida.",
    suggestedCapabilities: Object.freeze([]),
  },
  {
    key: "gym",
    label: "Gimnasio",
    description: "Sugiere membresías y mantiene disponibles el resto de las capacidades opcionales.",
    suggestedCapabilities: Object.freeze(["memberships"]),
  },
  {
    key: "restaurant",
    label: "Restaurante",
    description: "Sugiere carta, mesas, reservas, comandas y recetas sobre la operación existente.",
    suggestedCapabilities: Object.freeze([
      "gastronomicMenu",
      "tables",
      "tableReservations",
      "kitchenOrders",
      "recipes",
    ]),
  },
]);

export function normalizeProjectBusinessProfile(value) {
  const normalized = String(value || "").trim().toLowerCase();
  return PROJECT_BUSINESS_PROFILES.some(({ key }) => key === normalized) ? normalized : "";
}

export function projectBusinessProfileDefinition(value) {
  const normalized = normalizeProjectBusinessProfile(value);
  return PROJECT_BUSINESS_PROFILES.find(({ key }) => key === normalized) || null;
}

export function capabilitiesForBusinessProfile(value) {
  const profile = projectBusinessProfileDefinition(value);
  const suggested = new Set(profile?.suggestedCapabilities || []);
  return Object.fromEntries(
    CAPABILITY_KEYS.map((key) => [key, suggested.has(key)]),
  );
}

export function applyBusinessProfileSuggestions(currentCapabilities, value) {
  const current = normalizeProjectCapabilities(currentCapabilities);
  const suggested = capabilitiesForBusinessProfile(value);

  return Object.fromEntries(
    CAPABILITY_KEYS.map((key) => [key, current[key] === true || suggested[key] === true]),
  );
}
